import React, { useState, useEffect } from "react";
import {
  Stack,
  Grid,
  Paper,
  Text,
  ActionIcon,
  Box,
  Loader,
  Center,
} from "@mantine/core";
import { IconTrash, IconPhoto } from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { ImageInfo } from "../../../types/staticWebGenerator";
import { BusinessInfo } from "../../../types/context";
import ImageUpload from "./ImageUpload";
import { marketingLibraryImages } from "../utils/photoUtils";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";

const WebsitePhotos: React.FC = () => {
  const { getImages, deleteImage, uploadImage } = useApp();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Marketing library: GET /images/{salonId} only (SSG); originals only in UI
  useEffect(() => {
    const fetchImages = async () => {
      if (!businessInfo?.id) return;

      try {
        setIsLoading(true);
        const response = await getImages({
          salonId: businessInfo.id,
        });
        setImages(marketingLibraryImages(response.images));
      } catch (error) {
        console.error("Error fetching images:", error);
        notifyError("Failed to load images. Please try again.", {
          title: "Error",
          autoClose: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, [businessInfo?.id, getImages]);

  // Handle image upload success
  const handleUploadSuccess = async () => {
    if (!businessInfo?.id) return;

    try {
      const response = await getImages({
        salonId: businessInfo.id,
      });
      setImages(marketingLibraryImages(response.images));
      notifySuccess("Image uploaded successfully", {
        title: "Success",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error refreshing images after upload:", error);
    }
  };

  // Handle image delete
  const handleDeleteImage = async (image: ImageInfo) => {
    if (!businessInfo?.id || !image.filename) return;

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete ${image.filename}?`)) {
      return;
    }

    try {
      setIsDeleting(image.filename);
      await deleteImage({
        salonId: businessInfo.id,
        filename: image.filename,
        delete_thumbnail: true,
      });

      // Remove from local state
      setImages((prev) =>
        prev.filter((img) => img.filename !== image.filename),
      );

      notifySuccess("Image deleted successfully", {
        title: "Success",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting image:", error);
      notifyError("Failed to delete image. Please try again.", {
        title: "Error",
        autoClose: 5000,
      });
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <Stack gap="lg">
      {/* Upload Section */}
      <ImageUpload
        title="Your Photos"
        description="Upload your photos to personalize your website."
        buttonText="Upload Photos"
        onUpload={handleUploadSuccess}
      />

      {/* Images List Section */}
      <Box>
        <Text size="xl" fw={700} mb="md">
          Your Uploaded Photos
        </Text>

        {isLoading ? (
          <Center p="xl">
            <Loader size="md" />
          </Center>
        ) : images.length === 0 ? (
          <Paper p="xl" style={{ textAlign: "center" }}>
            <IconPhoto
              size={48}
              color="#ccc"
              style={{ margin: "0 auto 16px" }}
            />
            <Text c="dimmed">No images uploaded yet</Text>
            <Text size="sm" c="dimmed" mt="xs">
              Upload your first photo above to get started
            </Text>
          </Paper>
        ) : (
          <Grid gutter="md">
            {images.map((image) => (
              <Grid.Col
                key={image.filename}
                span={{ base: 12, sm: 6, md: 4, lg: 3 }}
              >
                <Paper
                  p="md"
                  style={{
                    border: `1px solid ${COLOR_BORDER_LIGHT}`,
                    borderRadius: "8px",
                    position: "relative",
                  }}
                >
                  {/* Image Preview */}
                  <Box
                    style={{
                      width: "100%",
                      height: "200px",
                      backgroundColor: COLOR_BACKGROUND_LIGHT,
                      borderRadius: "4px",
                      marginBottom: "8px",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={image.url || image.s3_url}
                      alt={image.filename}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        // Fallback if image fails to load
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </Box>

                  {/* Filename */}
                  <Text size="sm" c="dimmed" truncate title={image.filename}>
                    {image.filename}
                  </Text>

                  {/* File Size */}
                  <Text size="xs" c="dimmed" mt="xs">
                    {(image.size / 1024).toFixed(1)} KB
                  </Text>

                  {/* Delete Button */}
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    size="sm"
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                    }}
                    onClick={() => handleDeleteImage(image)}
                    disabled={isDeleting === image.filename}
                    loading={isDeleting === image.filename}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Box>
    </Stack>
  );
};

export default WebsitePhotos;
